import {
    Component,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    signal,
    inject,
    OnInit,
    DestroyRef,
    HostListener
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take, withLatestFrom } from 'rxjs/operators';
import { environment } from '@env/environment';
import { ChatwootService } from '@core/services/services/chatwoot.service';
import { RequestCreateService } from '@core/services/services/request-create.service';
import { SettingsSelectors } from '@store/settings';
import { AuthSelectors } from '@store/auth';
import { animate, style, transition, trigger } from '@angular/animations';

interface HubAction {
    id: string;
    icon: string;
    label: string;
    available: boolean;
}

@Component({
    imports: [MatIconModule, MatTooltipModule],
    selector: 'app-floating-hub',
    templateUrl: './floating-hub.component.html',
    styleUrls: ['./floating-hub.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('menuAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(8px) scale(0.95)' }),
                animate('150ms ease-out', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
            ]),
            transition(':leave', [
                animate('100ms ease-in', style({ opacity: 0, transform: 'translateY(8px) scale(0.95)' }))
            ])
        ])
    ]
})
export class FloatingHubComponent implements OnInit {
    private readonly chatwootService = inject(ChatwootService);
    private readonly requestCreateService = inject(RequestCreateService);
    private readonly settingsSelectors = inject(SettingsSelectors);
    private readonly authSelectors = inject(AuthSelectors);
    private readonly destroyRef = inject(DestroyRef);

    readonly expanded = signal(false);
    readonly actions = signal<HubAction[]>([]);
    readonly hasUnread = signal(false);
    private readonly chatwootMessageHandler = (): void => {
        if (!this.expanded()) {
            this.hasUnread.set(true);
        }
    };

    ngOnInit(): void {
        this.settingsSelectors.organisation$
            .pipe(
                filter(org => !!org),
                withLatestFrom(this.authSelectors.user$),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([organization]) => {
                this.buildActions(organization);
            });

        this.listenForUnread();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        if (!target.closest('.floating-hub')) {
            this.expanded.set(false);
        }
    }

    toggle(): void {
        this.expanded.update(v => !v);
    }

    onAction(action: HubAction): void {
        this.expanded.set(false);

        switch (action.id) {
            case 'chat':
                this.chatwootService.openChat();
                this.hasUnread.set(false);
                break;
            case 'add-request':
                this.requestCreateService.open();
                break;
        }
    }

    private buildActions(organization: any): void {
        const items: HubAction[] = [];

        const perms = organization.user_permissions || [];
        const hasAddRequest = perms.some(
            (p: string) =>
                p?.includes('cockpit.requests') ||
                p?.includes('uops.support.requests') ||
                p?.includes('ucontrol.support.requests')
        );

        if (hasAddRequest) {
            items.push({
                id: 'add-request',
                icon: 'heroicons_outline:plus-circle',
                label: 'Add support request',
                available: true
            });
        }

        const hasChatPermission = perms.some((p: string) => p?.includes('cockpit.chat'));
        const hasChatwoot =
            hasChatPermission &&
            environment.chatwoot?.enabled &&
            organization.connections?.some((c: any) => c.connectable_type === 'chatwoot' && c.status === 'active');

        if (hasChatwoot) {
            items.push({
                id: 'chat',
                icon: 'heroicons_outline:chat-bubble-left-right',
                label: 'Chat with us',
                available: true
            });
        }

        this.actions.set(items);
    }

    private listenForUnread(): void {
        window.addEventListener('chatwoot:on-message', this.chatwootMessageHandler);
        this.destroyRef.onDestroy(() => {
            window.removeEventListener('chatwoot:on-message', this.chatwootMessageHandler);
        });
    }
}
